import { useEffect, useMemo, useState } from 'react';
import type { CwTicketTypeAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwTicketTypeAliases, useSetCwTicketTypeAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwBoardName: string;
  ticketTypeId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `ticket-type-alias-${aliasSeq}`;
}

interface TicketTypeAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise ticket board (a dropdown) onto a PSA ticket type (a dropdown). On
 * the tickets sync, each CW ticket's board is resolved through these rows to set the
 * synced ticket's type. A CW board with no row here leaves the ticket type unset, so the
 * sync never fails on a missing alias.
 */
export function TicketTypeAliasEditor({ isOpen }: TicketTypeAliasEditorProps): JSX.Element {
  const configQuery = useCwTicketTypeAliases(isOpen);
  const saveAliases = useSetCwTicketTypeAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwBoardName: alias.cwBoardName,
        ticketTypeId: alias.ticketTypeId,
      })),
    );
  }, [config]);

  const ticketTypes = config?.ticketTypes ?? [];
  const discoveredBoards = config?.discoveredBoards ?? [];
  const live = config?.live ?? false;

  // CW board names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwBoardName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredBoards.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwBoardName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwBoardName, ticketTypeId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwTicketTypeAliasDto[] = rows
      .filter((row) => row.cwBoardName.trim() && row.ticketTypeId)
      .map((row) => ({
        cwBoardName: row.cwBoardName.trim(),
        ticketTypeId: row.ticketTypeId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-ticket-boards';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Ticket type</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise tickets live on a board. Map each ConnectWise board to a PSA ticket type
          below. On sync, each ticket&apos;s type is set from its mapped PSA type — ConnectWise
          boards without a row here leave the ticket type unset.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading ticket types…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load ticket types.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise ticket boards loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise ticket boards discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredBoards.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the ticket-type aliases. Make sure each row targets a valid ticket
              type.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No ticket-type aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise board</th>
                  <th className="py-2 pr-3 font-medium">PSA ticket type</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwBoardName}
                        placeholder="e.g. Help Desk"
                        onChange={(e) => updateRow(row.id, { cwBoardName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.ticketTypeId}
                        onChange={(e) => updateRow(row.id, { ticketTypeId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select type —</option>
                        {ticketTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save ticket types
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
